import useTrackEvent from '@/composables/mixpanel/useTrackEvent';
import { eventNames, MIXPANEL_ALLOWED_KEYS } from '@/constants/eventNames';
import api from '@/services/axios';
import { useDebounceFn } from '@vueuse/core';
import { ref } from 'vue';
import { useAppStore } from '../appStore';

export const createBaseStore = (resourceName) => {
  const appStore = useAppStore();
  const items = ref([]);
  const debouncedUpdates = new Map();
  const pagination = ref({
    total: 0,
    next_page: null,
    prev_page: null,
    next_url: null,
    prev_url: null,
    page: 1,
    per_page: 25,
    sort_by: 'last_modified_desc',
  });
  const cache = ref(new Map());

  const trackMixPanelEvent = ({ response, eventName, label, data }) => {
    if (response.status >= 200 && response.status < 300) {
      const eventData = {
        eventName: eventName,
        label: label,
        sendImmediately: true,
      };

      if (data) {
        eventData.data = {};
        for (let index of Object.keys(data)) {
          if (MIXPANEL_ALLOWED_KEYS.includes(index) && data[index]) eventData.data[index] = data[index];
        }
      }
      useTrackEvent(eventData);
    }
  };

  const setPagination = (response) => {
    const { next, previous } = response || {};

    const nextUrl = next ? new URL(next) : null;
    const previousUrl = previous ? new URL(previous) : null;

    // DRF omits ?page=1, so default to 1 when param is missing
    const nextPage = nextUrl ? parseInt(nextUrl.searchParams.get('page')) || 2 : null;
    const prevPage = previousUrl ? parseInt(previousUrl.searchParams.get('page')) || 1 : null;

    // Derive current page from neighbors
    let currentPage;
    if (nextPage) {
      currentPage = nextPage - 1;
    } else if (prevPage) {
      currentPage = prevPage + 1;
    } else {
      currentPage = 1;
    }

    pagination.value = {
      total: response?.count ?? 0,
      next_url: next ?? null,
      prev_url: previous ?? null,
      next_page: nextPage,
      prev_page: prevPage,
      per_page: 25,
      page: currentPage,
      sort_by: pagination.value.sort_by ?? 'last_modified_desc',
    };
  };
  const fetchItems = async (itemId = null, overrideUrl, query_param = {}) => {
    if (itemId && cache.value.has(itemId)) {
      return cache.value.get(itemId);
    }

    appStore.setLoading(true);
    try {
      let url = overrideUrl ? overrideUrl : itemId ? `/${resourceName}/${itemId}/` : `/${resourceName}/`;

      if (query_param && Object.keys(query_param).length > 0) {
        const queryString = new URLSearchParams(query_param).toString();
        url += `?${queryString}`;
      }

      const response = await api.get(url);

      if (itemId) {
        trackMixPanelEvent({
          response,
          eventName: eventNames.resource.selected,
          label: `User selecting ${resourceName}/${itemId}`,
        });

        const item = response.data;

        if (item) {
          cache.value.set(itemId, item);
          return item;
        }
        return null;
      } else {
        setPagination(response.data);
        trackMixPanelEvent({
          response,
          eventName: eventNames.resource.selected,
          label: `User selecting ${resourceName}`,
        });

        const itemsList = response.data.results || [];

        items.value = itemsList;
        itemsList.forEach((item) => {
          if (item && item.code) {
            cache.value.set(item.code, item);
          }
        });

        return itemsList;
      }
    } catch (error) {
      console.error(`Error fetching ${resourceName}:`, error);

      return { error: error.response || {} };
    } finally {
      appStore.setLoading(false);
    }
  };

  const storeItem = async (credentials, overrideUrl) => {
    appStore.setLoading(true);
    try {
      const response = await api.post(`/${resourceName}/`, credentials);

      trackMixPanelEvent({
        response,
        eventName: eventNames.resource.created,
        label: `User creating ${resourceName}`,
        data: credentials,
      });
      addItemToStore(response.data);
      return response.data;
    } catch (error) {
      console.error(`Error creating ${resourceName}:`, error);
      return { error: error.response || {} };
    } finally {
      appStore.setLoading(false);
    }
  };

  const getDebouncedUpdate = (id) => {
    if (!debouncedUpdates.has(id)) {
      debouncedUpdates.set(
        id,
        useDebounceFn(async (data) => {
          appStore.setLoading(true);
          try {
            const response = await api.put(`/${resourceName}/${id}/`, data);
            trackMixPanelEvent({
              response,
              eventName: eventNames.resource.updated,
              label: `User updating ${resourceName}`,
              data: data,
            });
            updateItemInStore({ code: id, ...response.data });
            return response.data;
          } catch (error) {
            throw error;
          } finally {
            appStore.setLoading(false);
          }
        }, 500)
      );
    }
    return debouncedUpdates.get(id);
  };

  const updateItem = async (id, data) => {
    try {
      appStore.setLoading(true);
      // Update the store immediately for responsive UI
      updateItemInStore({ code: id, ...data });

      // Make debounced API call (per-ID so different modules don't cancel each other)
      return await getDebouncedUpdate(id)(data);
    } catch (error) {
      console.error(`Error updating ${resourceName}:`, error);
      return { error: error.response || {} };
    } finally {
      appStore.setLoading(false);
    }
  };

  const addItemToStore = (item) => {
    updateItemInStore(item);
  };

  const removeItemFromStore = (id) => {
    items.value = items.value.filter((item) => item.code !== id);
    cache.value.delete(id);
    debouncedUpdates.delete(id);
  };

  const updateItemInStore = (updateItem) => {
    const index = items.value.findIndex((item) => item.code === updateItem.code);
    if (index !== -1) {
      items.value[index] = { ...items.value[index], ...updateItem };
      sortItems(pagination.value.sort_by);
    } else {
      items.value.push(updateItem);
    }
    cache.value.set(updateItem.code, updateItem);
  };

  const destroy = async (ids) => {
    const isBulk = Array.isArray(ids);
    if (!isBulk) {
      ids = [ids];
    }
    appStore.setLoading(true);
    try {
      const response = await api.delete(`/${resourceName}/bulk-delete/`, { data: { codes: ids } });
      for (const id of ids) removeItemFromStore(id);
      trackMixPanelEvent({
        response,
        eventName: eventNames.resource.deleted,
        label: `User deleting ${resourceName}`,
      });
      return { success: true };
    } catch (error) {
      console.error(`Error deleting ${resourceName}:`, error);
      return { error: error.response || {} };
    } finally {
      appStore.setLoading(false);
    }
  };

  /**
   * Sort existing items without making an api call.
   */
  const sortItems = () => {
    const sortBy = pagination.value?.sort_by;
    if (typeof sortBy !== 'string' || !sortBy) return;
    const isDesc = sortBy.endsWith('_desc');
    const field = sortBy.replace(/_(asc|desc)$/, '');
    const dateFields = ['last_modified', 'created_at', 'updated_at'];
    const isDateField = dateFields.includes(field);

    items.value.sort((a, b) => {
      if (isDateField) {
        const dateA = a[field] ? new Date(a[field]) : 0;
        const dateB = b[field] ? new Date(b[field]) : 0;
        const diff = dateA - dateB;

        return isDesc ? -diff : diff;
      } else {
        const valA = String(a[field] ?? '');
        const valB = String(b[field] ?? '');

        return isDesc ? valB.localeCompare(valA) : valA.localeCompare(valB);
      }
    });
  };
  return {
    pagination,
    items,
    fetchItems,
    storeItem,
    destroy,
    updateItem,
    addItemToStore,
    removeItemFromStore,
    updateItemInStore,
    sortItems,
  };
};
